/**
 * Settings -> Remote Connections (Remote Studio spec §1; the web interface of
 * 2026-10-31 grew into it). Like PlayIt Live's: everything is set here.
 *
 * - The station's hostname and access code (from the platform's dashboard), the port
 *   (changeable only while stopped), Use HTTPS, Start / Stop Server.
 * - Advanced: override the detected public address; no DNS updates on a spare install.
 * - The live status: registration, public address, DNS, certificate and its expiry,
 *   reachability, connected presenters, and the address to give presenters.
 * - Help me set up external access (firewall, router, probe, CGNAT).
 *
 * Engine side: nova/remote/studio.py, nova/api/webserver.py, nova/remote/access.py.
 */

import { useCallback, useEffect, useState } from "react";
import { engineBase } from "../api";
import { bus } from "../core/bus";
import { useApp } from "../core/app";
import { useSession } from "../core/session";
import { Row } from "./SettingsDialog";
import { toast } from "./Toast";

/** A window open on the live link (the station window aside). */
interface Connection { id: string; name: string; username: string | null; network: boolean }
interface Status {
  hostname: string; rootDomain: string; fqdn: string | null; hasAccessCode: boolean; https: boolean;
  ipOverrideOn: boolean; ipOverride: string; ddnsOff: boolean; installId: string; port: number; url: string | null;
  registration: { state: string; message: string | null };
  publicIp: string | null;
  dns: { status: string | null; message: string | null; at: string | null };
  certificate: { status: string; expires: string | null; message: string | null };
  reachability: { reachable: boolean | null; latencyMs: number | null; error: string | null; at: string | null };
  server: { running: boolean; port: number; error: string | null; https: boolean; addresses: string[] };
  connections: Connection[];
  studioConnected?: boolean;
  error?: string | null;
}
interface AccessStep { step: string; ok: boolean | null; message: string; how: string | null }

async function call(path: string, body?: unknown): Promise<Status> {
  const res = await fetch(`${engineBase()}/api/remote${path}`, body === undefined ? undefined : {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  return (await res.json()) as Status;
}

/** The port lives with the server switch (nova/api/webserver.py). */
async function setServerPort(port: number) {
  const r = (await fetch(`${engineBase()}/api/web`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ port }) })
    .then((x) => x.json()).catch(() => ({ error: "The engine did not answer." }))) as { error?: string };
  if (r.error) toast(r.error, "bad");
}

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");
const REGISTRATION: Record<string, string> = {
  off: "Not connected to the platform", registering: "Registering…", registered: "Registered",
  rejected: "Refused", error: "Could not reach the platform",
};

function StatusLine({ label, good, text, detail }: { label: string; good: boolean | null; text: string; detail?: string | null }) {
  return (
    <div className="nv-remote-status-row">
      <span className={`nv-remote-dot ${good === true ? "ok" : good === false ? "bad" : ""}`} aria-hidden="true" />
      <b>{label}</b>
      <span>{text}{detail ? <em className="nv-dim"> · {detail}</em> : null}</span>
    </div>
  );
}

export function WebSettings() {
  const app = useApp();
  const session = useSession();
  const [s, setS] = useState<Status | null>(null);
  const [hostname, setHostname] = useState("");
  const [code, setCode] = useState("");
  const [showCode, setShowCode] = useState(false);
  const [port, setPort] = useState("");
  const [ip, setIp] = useState("");
  const [busy, setBusy] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [help, setHelp] = useState<{ steps: AccessStep[]; publicIp: string | null } | null>(null);

  const take = useCallback((next: Status) => {
    setS(next);
    setHostname((h) => (document.activeElement?.id === "rs-host" ? h : next.hostname));
    setPort((p) => (document.activeElement?.id === "rs-port" ? p : String(next.server.port)));
    setIp((v) => (document.activeElement?.id === "rs-ip" ? v : next.ipOverride));
  }, []);
  const load = useCallback(() => void call("").then(take).catch(() => undefined), [take]);
  useEffect(() => {
    load();
    const offs = ["remote.changed", "web.changed", "remote.presence", "remote.notice"].map((t) => bus.on(t, load));
    // Registration, DNS and the certificate move in the background: look again every few seconds.
    const timer = window.setInterval(load, 5000);
    const notice = bus.on("remote.notice", (e) => toast(String((e.data as { message?: string })?.message ?? ""), "ok"));
    return () => {
      offs.forEach((off) => off());
      notice();
      window.clearInterval(timer);
    };
  }, [load]);
  if (!s) return <p className="nv-dim">Reading…</p>;

  const run = async (path: string, body?: unknown) => {
    setBusy(true);
    const next = await call(path, body ?? {}).catch(() => null);
    setBusy(false);
    if (!next) return toast("The engine did not answer.", "bad");
    if (next.error) toast(next.error, "bad");
    if (next.hostname !== undefined) take(next);
    return next;
  };
  const save = (body: Record<string, unknown>) => run("/settings", body);
  const running = s.server.running;

  return (
    <>
      <p>Lets presenters use this Nova from anywhere, in a browser: the same desk, live, and their mic on air. Enter the station's hostname and access code from your dashboard, then start the server.</p>
      {!session.enabled && (
        <div className="nv-update failed" role="alert">
          <div className="nv-update-head">
            <b>Turn on accounts first.</b>
            <span className="nv-dim">Presenters sign in with their own name; without accounts anyone could change your library.</span>
          </div>
          <button className="nv-btn sm" onClick={() => app.openSettings("users")}>Users & roles…</button>
        </div>
      )}

      <Row label="Hostname" hint={`Your station's name, such as kfm (or kfm.${s.rootDomain}).`}>
        <input id="rs-host" className="nv-input" style={{ width: 220 }} value={hostname} placeholder="kfm" disabled={busy}
          onChange={(e) => setHostname(e.target.value)}
          onBlur={() => { if (hostname.trim() !== s.hostname) void save({ hostname }); }} />
      </Row>
      <Row label="Access code" hint={s.hasAccessCode ? "Saved (encrypted on this computer). Type a new one to replace it." : "From your dashboard."}>
        <span className="nv-remote-code">
          <input className="nv-input" style={{ width: 220 }} type={showCode ? "text" : "password"} value={code} autoComplete="off"
            placeholder={s.hasAccessCode ? "••••-••••-••••" : "XXXX-XXXX-XXXX"} disabled={busy}
            onChange={(e) => setCode(e.target.value)}
            onBlur={() => { if (code.trim()) void save({ accessCode: code.trim() }).then(() => setCode("")); }} />
          <button className="nv-btn sm" onClick={() => setShowCode(!showCode)} aria-pressed={showCode}>{showCode ? "Hide" : "Show"}</button>
        </span>
      </Row>
      <Row label="Server port" hint={running ? "Stop the server to change it." : "The number in the address; 25433 unless another program uses it."}>
        <input id="rs-port" className="nv-input" style={{ width: 100 }} inputMode="numeric" value={port} disabled={busy || running}
          onChange={(e) => setPort(e.target.value.replace(/\D/g, ""))}
          onBlur={() => { if (port && Number(port) !== s.server.port) void setServerPort(Number(port)).then(load); }} />
      </Row>
      <Row label="Use HTTPS" hint={s.https ? "On: needed for presenters' microphones in the browser." : "Off: presenters' microphones will not work (browsers allow them only on https)."}>
        <label className="nv-toggle">
          <input type="checkbox" role="switch" checked={s.https} disabled={busy} onChange={(e) => void save({ https: e.target.checked })} />
          <i aria-hidden="true" />
        </label>
      </Row>

      <div className="nv-remote-actions">
        {!running ? (
          <button className="nv-btn primary" disabled={busy || !session.enabled} onClick={() => void run("/start")}>Start Server</button>
        ) : (
          <button className="nv-btn" disabled={busy} onClick={() => void run("/stop")}>Stop Server</button>
        )}
        <button className="nv-btn" disabled={busy || !running} onClick={() => void (async () => {
          setBusy(true);
          const r = await fetch(`${engineBase()}/api/remote/access`, { method: "POST", body: "{}" }).then((x) => x.json()).catch(() => null);
          setBusy(false);
          if (r) setHelp(r as { steps: AccessStep[]; publicIp: string | null });
          load();
        })()}>Help me set up external access</button>
        <button className="nv-btn sm" onClick={() => setAdvanced(!advanced)} aria-expanded={advanced}>{advanced ? "Hide advanced" : "Advanced"}</button>
      </div>
      {s.server.error && <p className="nv-signin-error" role="alert">{s.server.error}</p>}

      {advanced && (
        <div className="nv-remote-advanced">
          <Row label="Override detected IP" hint="Tell the platform this address instead of the one it sees.">
            <span className="nv-remote-code">
              <input type="checkbox" checked={s.ipOverrideOn} onChange={(e) => void save({ ipOverrideOn: e.target.checked })} aria-label="Override detected IP" />
              <input id="rs-ip" className="nv-input" style={{ width: 160 }} value={ip} placeholder="203.0.113.7" disabled={!s.ipOverrideOn}
                onChange={(e) => setIp(e.target.value)} onBlur={() => { if (ip !== s.ipOverride) void save({ ipOverride: ip }); }} />
            </span>
          </Row>
          <Row label="Disable DNS updates on this install" hint="For a spare computer that shares the hostname: it registers, but never moves the name to itself.">
            <label className="nv-toggle">
              <input type="checkbox" role="switch" checked={s.ddnsOff} onChange={(e) => void save({ ddnsOff: e.target.checked })} />
              <i aria-hidden="true" />
            </label>
          </Row>
        </div>
      )}

      <div className="nv-remote-status" aria-live="polite">
        <h5>Status</h5>
        <StatusLine label="Server" good={running} text={running ? `Running on port ${s.server.port}${s.server.https ? " (https)" : ""}` : "Stopped"} />
        <StatusLine label="Registration" good={s.registration.state === "registered" ? true : s.registration.state === "rejected" || s.registration.state === "error" ? false : null}
          text={REGISTRATION[s.registration.state] ?? s.registration.state} detail={s.registration.message} />
        <StatusLine label="Public IP" good={s.publicIp ? true : null} text={s.publicIp ?? "Not known yet"} />
        <StatusLine label="DNS" good={s.dns.status === "propagated" ? true : s.dns.status === "error" ? false : null}
          text={s.dns.status === "propagated" ? "Points here" : s.dns.status === "off" ? "Updates are off on this install" : s.dns.status ?? "Not updated yet"}
          detail={s.dns.message ?? (s.dns.at ? `checked ${when(s.dns.at)}` : null)} />
        <StatusLine label="Certificate" good={s.certificate.status === "issued" ? true : s.certificate.status === "failed" ? false : null}
          text={s.certificate.status === "issued" ? `Issued, until ${when(s.certificate.expires)}` : s.certificate.status === "pending" ? "Being issued…" : s.certificate.status === "failed" ? "Failed" : "None yet (this computer's own is used)"}
          detail={s.certificate.message} />
        <StatusLine label="Reachable" good={s.reachability.reachable}
          text={s.reachability.reachable ? `Yes${s.reachability.latencyMs != null ? `, ${s.reachability.latencyMs} ms` : ""}` : s.reachability.reachable === false ? "Not from the internet" : "Not tested yet"}
          detail={s.reachability.error} />
        <StatusLine label="On air window" good={s.studioConnected === true ? true : running ? false : null}
          text={s.studioConnected ? "Connected: remote desks can control the station" : "Not connected: open the Radio layout in Nova on this computer"} />
        <StatusLine label="Connected" good={null}
          text={s.connections.filter((c) => c.network).length ? s.connections.filter((c) => c.network).map((c) => c.name).join(", ") : "Nobody connected"} />
        {s.url && (
          <div className="nv-web-address nv-remote-url">
            <code>{s.url}</code>
            <button className="nv-btn sm" onClick={() => void navigator.clipboard?.writeText(s.url!).then(() => toast("Address copied.", "ok"))}>Copy</button>
          </div>
        )}
        {running && (
          <div className="nv-setrow">
            <div>
              <b>On this network</b>
              <span>Presenters in the building can also use these. The first time, Windows may ask whether Nova may use the network: choose Allow.{s.server.https && " The browser warns about this computer's own certificate; that is expected here."}</span>
            </div>
            <div className="nv-web-addresses">
              {s.server.addresses.map((a) => (
                <div key={a} className="nv-web-address">
                  <code>{a}</code>
                  <button className="nv-btn sm" onClick={() => void navigator.clipboard?.writeText(a).then(() => toast("Address copied.", "ok"))}>Copy</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {help && (
        <div className="nv-remote-help">
          <h5>External access</h5>
          {help.steps.map((h) => (
            <div key={h.step} className="nv-remote-help-step">
              <StatusLine label={{ firewall: "Firewall", router: "Router", probe: "From the internet", cgnat: "Internet provider" }[h.step] ?? h.step}
                good={h.ok} text={h.message} />
              {h.how && <p className="nv-dim">{h.how}</p>}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
